"use client";

import { useTranslations } from "use-intl";

import { DealOfferPriceSignalsRow } from "@/components/marketplace/deal-offer-price-signals-row";
import { DealOfferActionCta } from "@/components/marketplace/deal-offer-shared";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import {
  dealOfferShippingCountsTowardTotal,
  effectiveDealPrice,
  isInternationalDealOffer,
} from "@/components/marketplace/deal-offers-utils";
import { Price } from "@/components/Price";
import { dealOfferShippingPriceForDisplay } from "@garimpa/lib/dealOfferShippingPriceForDisplay";

function offerCurrency(offer: DealOffer) {
  return offer.priceHistoryCurrency ?? offer.currentCurrency;
}

function shippingSupplement(offer: DealOffer) {
  if (!isInternationalDealOffer(offer)) return null;
  return dealOfferShippingPriceForDisplay({
    shippingPrice: offer.currentPriceShipping,
    shippingCountsTowardTotal: dealOfferShippingCountsTowardTotal(offer),
  });
}

function HeroPrice({ offer }: { offer: DealOffer }) {
  const t = useTranslations("shared.affiliates");
  const currency = offerCurrency(offer);
  const supplement = shippingSupplement(offer);

  return (
    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
      <Price
        amount={offer.currentPrice ?? effectiveDealPrice(offer)}
        currency={currency}
        fallback="—"
        className="text-4xl font-bold leading-none tracking-tight"
      />
      {supplement && (
        <span className="text-sm font-bold text-muted-foreground">
          {t.rich("shippingSupplement", {
            price: () => <Price amount={supplement} currency={currency} />,
          })}
        </span>
      )}
    </p>
  );
}

/** The store title gets the full panel width below the price and purchase action. */
function StoreTitle({ offer }: { offer: DealOffer }) {
  return (
    <p
      className="min-w-0 truncate text-[13px] leading-snug text-muted-foreground"
      title={offer.title}
    >
      “{offer.title}”
    </p>
  );
}

/**
 * Price and signals beside the purchase action; the store title sits below on its own line.
 */
export function BestDealOfferPriceBlock({
  offer,
  showTitle,
}: {
  offer: DealOffer;
  showTitle: boolean;
}) {
  const t = useTranslations("shared.affiliates");

  return (
    <div className="@container flex min-w-0 flex-col gap-3">
      <div className="flex flex-col gap-4 @lg:flex-row @lg:items-center @lg:justify-between @lg:gap-6">
        <div className="flex min-w-0 flex-col gap-2">
          <HeroPrice offer={offer} />
          <DealOfferPriceSignalsRow offer={offer} compact />
        </div>
        <DealOfferActionCta
          offer={offer}
          variant="outline"
          actionLabelOverride={t("buyAtStore", { store: offer.providerDisplayName })}
          className="w-full @lg:w-auto @lg:max-w-[55%] @lg:shrink-0"
        />
      </div>
      {showTitle && <StoreTitle offer={offer} />}
    </div>
  );
}
